import type { AuditType } from '@/lib/types';

/**
 * IRATA-aligned display labels for audit types.
 *
 * The backend stores ISO 17021 style values (`stage_1`, `stage_2`) and its
 * `audit_type_display` returns "Stage 1" / "Stage 2". H3Comply is an
 * IRATA/SPRAT product, so the UI surfaces IRATA assessment/surveillance/renewal
 * terminology instead. The stored values are unchanged — only the labels differ.
 */
export const AUDIT_TYPE_LABELS: Record<AuditType, string> = {
  stage_1: 'Initial Assessment (Document Review)',
  stage_2: 'Initial Assessment (On-Site)',
  surveillance: 'Surveillance Assessment',
  renewal: 'Renewal Assessment',
};

/** Ordered list for pickers. */
export const AUDIT_TYPE_OPTIONS: { value: AuditType; label: string }[] = (
  ['stage_1', 'stage_2', 'surveillance', 'renewal'] as AuditType[]
).map((value) => ({ value, label: AUDIT_TYPE_LABELS[value] }));

/** Resolve an IRATA label for an audit type, falling back to a server label. */
export function getAuditTypeLabel(
  auditType: AuditType | string | null | undefined,
  fallback?: string,
): string {
  if (auditType && auditType in AUDIT_TYPE_LABELS) {
    return AUDIT_TYPE_LABELS[auditType as AuditType];
  }
  return fallback ?? String(auditType ?? '');
}
